import { useRuntimeConfig } from '#imports'
import { type Component, type MaybeRefOrGetter, computed, toValue } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { IconQuality } from '@ds/icons/IconQuality'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'
import type { Location } from '@ds/types/Location'

import { useExpressReplacementUrl } from '~/scopes/navigation/composables/useExpressReplacementUrl'
import { useMobilePlansUrl } from '~/scopes/navigation/composables/useMobilePlansUrl'

import translations from './usePlpBlocks.translations'

type I18n = ReturnType<typeof useI18n>

const SMARTPHONE_SLUGS_BY_MARKET: Partial<
  Record<MarketCountryCode, ReadonlySet<string>>
> = {
  [MarketCountryCode.US]: new Set([
    'smartphones',
    'iphone',
    'samsung',
    'google-pixel',
    'android-smartphones',
  ]),
  [MarketCountryCode.FR]: new Set([
    'smartphones',
    'iphone-reconditionne',
    'samsung',
    'smartphone-google-reconditionne',
    'smartphones-android',
  ]),
}

export type PlpBlockColumns = 1 | 2

export type PlpBlockContent = {
  kind: 'warranty' | 'expressReplacement' | 'telcoSingle' | 'telcoMulti'
  variant: 'brandMid' | 'brandLow' | 'defaultMid'
  columns: PlpBlockColumns
  title: string
  description?: string
  ctaLabel?: string
  ctaUrl?: Location
  icon?: { src: Component | string; width?: number; height?: number }
  iconMobileOnly?: boolean
  image?: { src: string; width: number; height: number }
  trackingName: 'warranty' | 'express_replacement' | 'bouygues' | 'us_telco'
}

export type PlpBlocksData = {
  block1: PlpBlockContent
  block2?: PlpBlockContent
  block3?: PlpBlockContent
}

export type PlpBlockKey = keyof PlpBlocksData

export type PlpBlocksLayout = Partial<
  Record<PlpBlockKey, { columns: PlpBlockColumns }>
>

function getWarranty(i18n: I18n): PlpBlockContent {
  return {
    kind: 'warranty',
    variant: 'brandMid',
    columns: 1,
    title: i18n(translations.warrantyTitle),
    description: i18n(translations.warrantySubtitle),
    icon: { src: IconQuality },
    trackingName: 'warranty',
  }
}

function getExpressReplacement(
  i18n: I18n,
  expressReplacementUrl: string,
): PlpBlockContent {
  return {
    kind: 'expressReplacement',
    variant: 'brandLow',
    columns: 2,
    title: i18n(translations.expressReplacementTitle),
    description: i18n(translations.expressReplacementSubtitle),
    ctaLabel: i18n(translations.expressReplacementCta),
    ctaUrl: expressReplacementUrl,
    icon: { src: IconTruckExpress },
    image: {
      src: '/img/plp/new-express-replacement.webp',
      width: 915,
      height: 1080,
    },
    trackingName: 'express_replacement',
  }
}

function getTelco(
  slug: string | undefined,
  countryCode: string,
  i18n: I18n,
  mobilePlansUrl: Location | undefined,
): PlpBlockContent | undefined {
  if (
    countryCode === MarketCountryCode.FR &&
    SMARTPHONE_SLUGS_BY_MARKET[MarketCountryCode.FR]?.has(slug ?? '')
  ) {
    return {
      kind: 'telcoSingle',
      variant: 'defaultMid',
      columns: 1,
      title: i18n(translations.telcoSingleTitle),
      ctaLabel: i18n(translations.telcoSingleCta),
      ctaUrl: mobilePlansUrl,
      icon: { src: '/img/plp/telco-single-logos.svg', width: 117, height: 42 },
      trackingName: 'bouygues',
    }
  }

  if (
    countryCode === MarketCountryCode.US &&
    SMARTPHONE_SLUGS_BY_MARKET[MarketCountryCode.US]?.has(slug ?? '')
  ) {
    return {
      kind: 'telcoMulti',
      variant: 'brandLow',
      columns: 2,
      title: i18n(translations.telcoMultiTitle),
      description: i18n(translations.telcoMultiSubtitle),
      ctaLabel: i18n(translations.telcoMultiCta),
      ctaUrl: mobilePlansUrl,
      icon: { src: '/img/plp/telco-multi-logos.svg', width: 198, height: 42 },
      iconMobileOnly: true,
      image: {
        src: '/img/plp/telco-multi-block.webp',
        width: 613,
        height: 625,
      },
      trackingName: 'us_telco',
    }
  }

  return undefined
}

export function usePlpBlocks(slug: MaybeRefOrGetter<string | undefined>) {
  const { FF_ENABLE_SERVICES_REVAMP = '' } = useRuntimeConfig().public
  const { market } = useMarketplace()
  const i18n = useI18n()
  const { mobilePlansUrl } = useMobilePlansUrl()
  const { expressReplacementUrl } = useExpressReplacementUrl()

  const plpBlocks = computed<PlpBlocksData | undefined>(() => {
    if (!FF_ENABLE_SERVICES_REVAMP.includes(market.countryCode))
      return undefined

    const expressReplacement = getExpressReplacement(
      i18n,
      expressReplacementUrl.value,
    )
    const telco = getTelco(
      toValue(slug),
      market.countryCode,
      i18n,
      mobilePlansUrl.value,
    )

    return {
      block1: getWarranty(i18n),
      block2: expressReplacement ?? telco,
      block3: expressReplacement && telco,
    }
  })

  const blocksLayout = computed<PlpBlocksLayout | undefined>(() => {
    const blocks = plpBlocks.value
    if (!blocks) return undefined

    return (Object.keys(blocks) as PlpBlockKey[]).reduce<PlpBlocksLayout>(
      (layout, key) => {
        const block = blocks[key]

        return block ? { ...layout, [key]: { columns: block.columns } } : layout
      },
      {},
    )
  })

  return { plpBlocks, blocksLayout }
}
